import { ClipboardModule } from '@angular/cdk/clipboard';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import { CSP_NONCE, CUSTOM_ELEMENTS_SCHEMA, DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatBadgeModule } from '@angular/material/badge';

import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { MAT_RIPPLE_GLOBAL_OPTIONS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { MatExpansionModule } from '@angular/material/expansion';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { NgxMaterialTimepickerModule } from '@gerandon/ngx-material-timepicker';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AgencyComponent } from './components/agency/agency.component';
import { BudgetComponent } from './components/budget/budget.component';
import { InterventionListComponent } from './components/intervention-list/intervention-list.component';
import { LoginComponent } from './components/login/login.component';
import { NavMenuComponent } from './components/nav-menu/nav-menu.component';
import { OrderComponent } from './components/order/order.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { StratListComponent } from './components/strat-list/strat-list.component';
import { StrategyDetailComponent } from './components/strategy-detail/strategy-detail.component';
import { StrategyComponent } from './components/strategy/strategy.component';
import { UserConfigurationComponent } from './components/user-configuration/user-configuration.component';
import { ErrorComponent } from './error.component';
import { ConfigurationGuard } from './tools/configuration.guard';
import { CoreData } from './tools/core-data/core-data';
import { DragDropDirective } from './tools/directive/drag-drop-directive';
import { LongPressDirective } from './tools/directive/long-press.directive';
import { PreventDoubleClickDirective } from './tools/directive/prevent-double-click.directive';
import { LoginGuard } from './tools/login-guard';
import { AlternateContactBoxComponent } from './tools/ui/alternate-contact-box/alternate-contact-box.component';
import { ClientSelectionBoxDialog } from './tools/ui/client-selection-box/client-selection-box';
import { ComplementaryMeasurenBoxDialog } from './tools/ui/complementary-measure-box/complementary-measure-box';
import { ConfirmBoxDialog } from './tools/ui/confirm-box/confirm-box.component';
import { CustomSnackBarComponent } from './tools/ui/custom-snackbar/custom-snackbar.component';
import { EditTextBoxDialog } from './tools/ui/edit-text-box/edit-text-box';
import { ExportBoxDialog } from './tools/ui/export-box/export-box';
import { LoadingTask } from './tools/ui/loading-task';
import { LogAsBoxComponent } from './tools/ui/log-as-box/log-as-box.component';
import { MapSelectionMeasurenBoxDialog } from './tools/ui/map-selection-box/map-selection-box';
import { MultipleFileUploadBoxDialog } from './tools/ui/mutilple-file-upload-box/multiple-file-upload-box';
import { StandardMeasurenBoxDialog } from './tools/ui/standard-measure-box/standard-measure-box';
import { UserBoxComponent } from './tools/ui/user-box/user-box.component';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { secureApiInterceptor } from './tools/secure-api.interceptor';
import { MeasureEditionBoxDialog } from './tools/ui/standard-measure-edition-box/measure-edition-box';
import { OptionForSynthese } from './tools/ui/selection-category-synthese-chantier/option-for-synthese';
import { SelectionCategoryBoxDialog } from './tools/ui/selection-category-synthese-chantier/selection-category-synthese-chantier-box';
import { NewCustomerBoxComponent } from './tools/ui/new-customer-box/new-customer-box.component';
import { NumberBoxDialog } from './tools/ui/number-box/number-box';

const nonce = (
    document.querySelector('meta[name="CSP_NONCE"]') as HTMLMetaElement
)?.content;
@NgModule({
    declarations: [
        PreventDoubleClickDirective,
        LongPressDirective,
        AppComponent,
        ErrorComponent,
        LoginComponent,
        NavMenuComponent,
        CustomSnackBarComponent,
        DragDropDirective,
        ConfirmBoxDialog,
        MultipleFileUploadBoxDialog,
        ClientSelectionBoxDialog,
        StratListComponent,
        StrategyComponent,
        UserConfigurationComponent,
        OrderComponent,
        InterventionListComponent,
        StrategyDetailComponent,
        AlternateContactBoxComponent,
        UserBoxComponent,
        LogAsBoxComponent,
        BudgetComponent,
        ResetPasswordComponent,
        AgencyComponent,
        ComplementaryMeasurenBoxDialog,
        StandardMeasurenBoxDialog,
        MapSelectionMeasurenBoxDialog,
        EditTextBoxDialog,
        ExportBoxDialog,
        MeasureEditionBoxDialog,
        SelectionCategoryBoxDialog,
        NewCustomerBoxComponent,
        NumberBoxDialog
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [CommonModule,
        FormsModule,
        BrowserModule,
        ReactiveFormsModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        ClipboardModule,
        MatTableModule,
        MatSnackBarModule,
        MatFormFieldModule,
        MatExpansionModule,
        MatInputModule,
        MatButtonModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatDialogModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatPaginatorModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatSortModule,
        MatChipsModule,
        MatSlideToggleModule,
        MatButtonToggleModule,
        MatStepperModule,
        MatListModule,
        MatBadgeModule,
        MatMenuModule,
        TextFieldModule,
        ScrollingModule,
        MatCheckboxModule,
        NgCircleProgressModule.forRoot(),
        CalendarModule.forRoot({
            provide: DateAdapter,
            useFactory: adapterFactory,
        }),
        NgxMaterialTimepickerModule], providers: [
            { provide: LOCALE_ID, useValue: 'fr-FR' },
            { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
            { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
            CoreData,
            LoadingTask,
            LoginGuard,
            ConfigurationGuard,
            DatePipe,
            provideHttpClient(withXhr(), withInterceptors([secureApiInterceptor])),
            {
                provide: CSP_NONCE,
                useValue: nonce,
            },
        ]
})
export class AppModule { }

registerLocaleData(localeFr, 'fr', localeFrExtra);
